/**
 * 玩家回放 - 详情页 (Phase 2)
 *
 * 顶部: SessionMetadata 卡片
 * 中部: Tabs - 统一时间线 (TimelineView) / 事件 (EventsList) / ASR (AsrSubtitles)
 * 底部: ScreenshotCarousel
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card,
  Tabs,
  Space,
  Button,
  message,
  Result,
  Breadcrumb,
} from 'antd'
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons'

import { playbackApi } from '@/services/api'
import type { PlaybackSessionDetail } from '@/types/playback'

import SessionMetadata from './components/SessionMetadata'
import PlaybackRichDetailCard from './components/PlaybackRichDetailCard'
import TimelineView from './components/TimelineView'
import EventsList from './components/EventsList'
import AsrSubtitles from './components/AsrSubtitles'
import ScreenshotCarousel from './components/ScreenshotCarousel'

const PlaybackDetail: React.FC = () => {
  const { worldId, playerId } = useParams<{ worldId: string; playerId: string }>()
  const navigate = useNavigate()

  const [session, setSession] = useState<PlaybackSessionDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [activeTab, setActiveTab] = useState<'timeline' | 'events' | 'asr'>('timeline')
  // selectedEventId 由 TimelineView 内部消费；此处 setter 为未来事件双向定位预留
  const [selectedEventId] = useState<number | null>(null)
  const [selectedUtteranceSeq, setSelectedUtteranceSeq] = useState<number | null>(null)

  const fetchSession = useCallback(async () => {
    if (!worldId || !playerId) return
    setLoading(true)
    setError(null)
    try {
      const response = await playbackApi.getSession(worldId, playerId)
      setSession(response.data ?? null)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404) {
        setError('未找到该 (world, player) 的回放记录')
      } else {
        setError(err?.message ?? '加载会话元数据失败')
      }
      message.error(err?.message ?? '加载失败')
      console.error('fetchSession error:', err)
    } finally {
      setLoading(false)
    }
  }, [worldId, playerId])

  useEffect(() => {
    fetchSession()
  }, [fetchSession])

  if (!worldId || !playerId) {
    return (
      <Result
        status="error"
        title="缺少参数"
        subTitle="worldId 与 playerId 必须在 URL 中"
      />
    )
  }

  return (
    <div className="p-6">
      <Breadcrumb
        className="mb-4"
        items={[
          {
            title: (
              <Space>
                <HomeOutlined />
                <a onClick={() => navigate('/playback')}>回放中心</a>
              </Space>
            ),
          },
          { title: `${worldId.slice(0, 16)}… / ${playerId}` },
        ]}
      />

      <Space className="mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/playback')}>
          返回列表
        </Button>
        <Button onClick={fetchSession} loading={loading}>
          刷新元数据
        </Button>
      </Space>

      {error && !loading ? (
        <Result
          status="404"
          title="未找到回放会话"
          subTitle={error}
          extra={
            <Button type="primary" onClick={() => navigate('/playback')}>
              返回列表
            </Button>
          }
        />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <SessionMetadata session={session} loading={loading && !session} />
          <PlaybackRichDetailCard session={session} loading={loading && !session} />

          <Card>
            <Tabs
              activeKey={activeTab}
              onChange={(k) => setActiveTab(k as typeof activeTab)}
              items={[
                {
                  key: 'timeline',
                  label: '统一时间线',
                  children: (
                    <TimelineView
                      worldId={worldId}
                      playerId={playerId}
                      selectedEventId={selectedEventId}
                      selectedUtteranceSeq={selectedUtteranceSeq}
                    />
                  ),
                },
                {
                  key: 'events',
                  label: '仅事件',
                  children: <EventsList worldId={worldId} playerId={playerId} />,
                },
                {
                  key: 'asr',
                  label: '仅 ASR',
                  children: (
                    <AsrSubtitles
                      worldId={worldId}
                      playerId={playerId}
                      onSelect={setSelectedUtteranceSeq}
                      selectedSeq={selectedUtteranceSeq}
                    />
                  ),
                },
              ]}
            />
          </Card>

          <ScreenshotCarousel worldId={worldId} playerId={playerId} />
        </Space>
      )}
    </div>
  )
}

export default PlaybackDetail